import { CopyOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Input, message, Select, Space, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import { PromoCodeFormMode, PromoCodeItem, SearchPromoCodeParams } from 'Modules'
import { useCallback, useMemo, useState } from 'react'

import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_OPTIONS } from 'constants/pagination'
import {
  formatDistributionSummary,
  formatPromoCodeUsedTimes,
  formatPromoCodeValue,
  PROMO_CODE_SEARCH_MAX_LENGTH,
  PROMO_CODE_STATUS_CONFIG,
  PROMO_CODE_STATUS_OPTIONS,
  PROMO_CODE_TYPE_CONFIG,
  PROMO_CODE_TYPE_OPTIONS,
} from 'constants/promo-code'
import { usePromoCodes } from 'hooks/promo-code'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { PromoCodeAppliedPtosModal } from 'modules/promo-code/applied-ptos-modal'
import { DeletePromoCodeModal } from 'modules/promo-code/delete-promo-code-modal'
import { PromoCodeFormModal } from 'modules/promo-code/promo-code-form-modal'
import { WhitelistEmailModal } from 'modules/promo-code/whitelist/whitelist-email-modal'

const DATE_FORMAT = 'DD/MM/YYYY'
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm:ss'

const formatDate = (value?: string) => (value ? dayjs(value).format(DATE_FORMAT) : '-')
const formatDateTime = (value?: string) => (value ? dayjs(value).format(DATE_TIME_FORMAT) : '-')

interface CodeCellProps {
  code: string
  value: string
}

/** Code content + formatted value line, with a one-click copy icon. */
const CodeCell = ({ code, value }: CodeCellProps) => {
  const handleCopy = useCallback(() => {
    if (!navigator.clipboard) {
      message.error('Clipboard not available')
      return
    }
    navigator.clipboard.writeText(code).then(
      () => message.success('Copied to clipboard'),
      () => message.error('Failed to copy')
    )
  }, [code])

  return (
    <div className="promo-code-table__code">
      <Flex align="center" gap={6}>
        <span className="promo-code-table__code-content">{code}</span>
        <Tooltip title="Copy code">
          <CopyOutlined className="promo-code-table__copy" onClick={handleCopy} />
        </Tooltip>
      </Flex>
      <div className="promo-code-table__code-value">{value}</div>
    </div>
  )
}

export const PromoCodeTable = () => {
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.PROMO_CODE)

  const { searchParams, setSearchParams, initPagination } = useSearchParams<SearchPromoCodeParams>(
    {}
  )
  const [searchValue, setSearchValue] = useState('')
  const [promoCodeToDelete, setPromoCodeToDelete] = useState<PromoCodeItem | null>(null)

  // Apply a search/filter change: reset to page 1 but keep the current page size.
  // (useSearchParams' onSearch would also reset the page size back to default.)
  const applyFilter = useCallback(
    (input: Partial<SearchPromoCodeParams>) => {
      setSearchParams((prev) => ({ ...prev, ...input, page: DEFAULT_PAGE_INDEX }))
    },
    [setSearchParams]
  )

  const { promoCodes, total, isLoading, isFetching, refetch } = usePromoCodes(searchParams)

  const [formModal, setFormModal] = useState<{
    open: boolean
    mode: PromoCodeFormMode
    record: PromoCodeItem | null
  }>({ open: false, mode: 'create', record: null })

  const handleCreate = useCallback(
    () => setFormModal({ open: true, mode: 'create', record: null }),
    []
  )
  const handleViewDetails = useCallback(
    (record: PromoCodeItem) => setFormModal({ open: true, mode: 'view', record }),
    []
  )
  const [appliedPtosRecord, setAppliedPtosRecord] = useState<PromoCodeItem | null>(null)
  const handleViewAppliedPtos = useCallback(
    (record: PromoCodeItem) => setAppliedPtosRecord(record),
    []
  )
  const [whitelistRecord, setWhitelistRecord] = useState<PromoCodeItem | null>(null)
  const handleViewWhitelist = useCallback((record: PromoCodeItem) => setWhitelistRecord(record), [])

  const columns: ColumnsType<PromoCodeItem> = useMemo(() => {
    const sortOrderFor = (field: string) => {
      if (searchParams.sort === `${field}:asc`) return 'ascend' as const
      if (searchParams.sort === `${field}:desc`) return 'descend' as const
      return null
    }
    return [
      {
        title: 'Created Date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 140,
        sorter: true,
        sortOrder: sortOrderFor('createdAt'),
        render: (value: string) => formatDate(value),
      },
      {
        title: 'Promo Code',
        dataIndex: 'code',
        key: 'code',
        width: 200,
        render: (_, record) => <CodeCell code={record.code} value={formatPromoCodeValue(record)} />,
      },
      {
        title: 'Start Time',
        dataIndex: 'startDate',
        key: 'startDate',
        width: 180,
        sorter: true,
        sortOrder: sortOrderFor('startDate'),
        render: (value: string) => formatDateTime(value),
      },
      {
        title: 'End Time',
        dataIndex: 'endDate',
        key: 'endDate',
        width: 180,
        render: (value: string) => formatDateTime(value),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 150,
        render: (type: PromoCodeItem['type']) => PROMO_CODE_TYPE_CONFIG[type],
      },
      {
        title: 'Used Times',
        dataIndex: 'usedTimes',
        key: 'usedTimes',
        width: 130,
        sorter: true,
        sortOrder: sortOrderFor('usedTimes'),
        render: (_, record) => formatPromoCodeUsedTimes(record.usedTimes, record.totalSupply),
      },
      {
        title: 'Total PTOs Applied',
        dataIndex: 'totalPtosApplied',
        key: 'totalPtosApplied',
        width: 170,
        sorter: true,
        sortOrder: sortOrderFor('totalPtosApplied'),
        render: (value: number, record) => (
          <Button
            type="link"
            className="promo-code-table__applied-link"
            onClick={() => handleViewAppliedPtos(record)}
          >
            {value}
          </Button>
        ),
      },
      {
        title: 'Whitelist & Email',
        key: 'whitelist',
        width: 160,
        render: (_, record) => (
          <div>
            <Button
              type="link"
              className="promo-code-table__applied-link"
              onClick={() => handleViewWhitelist(record)}
            >
              {formatDistributionSummary(record.distribution)}
            </Button>
            {record.distribution?.totalEmailsSent != null && (
              <div className="promo-code-table__whitelist-sub">
                {record.distribution.totalEmailsSent} email
                {record.distribution.totalEmailsSent === 1 ? '' : 's'} sent
              </div>
            )}
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: PromoCodeItem['status']) => {
          const config = PROMO_CODE_STATUS_CONFIG[status]
          return (
            <Tag
              className="promo-code-table__status-tag"
              style={{
                color: config.color,
                backgroundColor: config.background,
                borderColor: 'transparent',
              }}
            >
              {config.label}
            </Tag>
          )
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 160,
        render: (_, record) => {
          // Delete is only available for a Scheduled code that has never been used.
          const canDelete = canEdit && record.status === 'SCHEDULED' && record.usedTimes === 0
          return (
            <Space size="middle">
              <Button
                type="link"
                className="promo-code-table__action-view"
                onClick={() => handleViewDetails(record)}
              >
                View Details
              </Button>
              {canDelete ? (
                <Button
                  type="link"
                  danger
                  className="promo-code-table__action-delete"
                  onClick={() => setPromoCodeToDelete(record)}
                >
                  Delete
                </Button>
              ) : null}
            </Space>
          )
        },
      },
    ]
  }, [searchParams.sort, canEdit, handleViewDetails, handleViewAppliedPtos, handleViewWhitelist])

  // Search on Search button click / Enter key.
  const handleSearch = () => {
    applyFilter({ keyword: searchValue.trim() || undefined })
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<PromoCodeItem> | SorterResult<PromoCodeItem>[]
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sort =
      singleSorter?.field && singleSorter.order
        ? `${String(singleSorter.field)}:${singleSorter.order === 'ascend' ? 'asc' : 'desc'}`
        : undefined

    setSearchParams((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      sort,
    }))
  }

  return (
    <Card className="promo-code-table" title="Promo Code List">
      <div className="promo-code-table__toolbar">
        <Flex gap={12} wrap="wrap" align="center" justify="space-between">
          <Flex gap={12} wrap="wrap" align="center" style={{ flex: 1, minWidth: 320 }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by code content"
              value={searchValue}
              maxLength={PROMO_CODE_SEARCH_MAX_LENGTH}
              onChange={(e) => {
                const next = e.target.value
                setSearchValue(next)
                // Clearing the field (allow-clear "x") resets the list immediately.
                if (!next) applyFilter({ keyword: undefined })
              }}
              onPressEnter={handleSearch}
              allowClear
              style={{ flex: 1, maxWidth: 320, minWidth: 200 }}
            />
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
            <Select<PromoCodeItem['status'] | 'ALL'>
              options={[{ value: 'ALL', label: 'All Status' }, ...PROMO_CODE_STATUS_OPTIONS]}
              value={searchParams.status ?? 'ALL'}
              onChange={(value) => applyFilter({ status: value === 'ALL' ? undefined : value })}
              style={{ width: 150 }}
            />
            <Select<PromoCodeItem['type'] | 'ALL'>
              options={[{ value: 'ALL', label: 'All Types' }, ...PROMO_CODE_TYPE_OPTIONS]}
              value={searchParams.type ?? 'ALL'}
              onChange={(value) => applyFilter({ type: value === 'ALL' ? undefined : value })}
              style={{ width: 160 }}
            />
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching} />
            </Tooltip>
          </Flex>
          <Button type="primary" icon={<PlusOutlined />} disabled={!canEdit} onClick={handleCreate}>
            Create Promo Code
          </Button>
        </Flex>

        <div className="promo-code-table__summary">
          Total codes: <strong>{total}</strong>
        </div>
      </div>

      <Table<PromoCodeItem>
        columns={columns}
        dataSource={promoCodes}
        rowKey={(record) => record.id}
        loading={isLoading}
        pagination={{
          ...initPagination(total),
          showSizeChanger: true,
          pageSizeOptions: DEFAULT_PAGE_OPTIONS,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1450 }}
      />

      <DeletePromoCodeModal
        open={!!promoCodeToDelete}
        promoCode={promoCodeToDelete}
        onClose={() => setPromoCodeToDelete(null)}
      />

      <PromoCodeFormModal
        open={formModal.open}
        mode={formModal.mode}
        record={formModal.record}
        canEdit={canEdit}
        onClose={() => setFormModal((prev) => ({ ...prev, open: false }))}
      />

      <PromoCodeAppliedPtosModal
        open={!!appliedPtosRecord}
        promoCode={appliedPtosRecord}
        onClose={() => setAppliedPtosRecord(null)}
      />

      <WhitelistEmailModal
        open={!!whitelistRecord}
        promoCode={whitelistRecord}
        canEdit={canEdit}
        onClose={() => setWhitelistRecord(null)}
      />
    </Card>
  )
}
